/**
 * PublicFormConsent — communication preferences on a public hosted form
 *
 * Shared by both hosted-form renderers (SubmitNdfForm and the legacy
 * SubmitForm) so the two can never drift apart on what a tick — or an
 * untick — means.
 *
 * The wording matters as much as the boxes: a returning contact's submission
 * REPLACES their previous preferences, so anything left clear is turned off.
 * People only understand that if the form says it (issue #2121).
 */
import { Checkbox } from '@respectly/ui';

export interface PublicFormConsentProps {
  showEmail: boolean;
  showSms: boolean;
  /** Tenant-authored disclosure shown above the boxes. */
  consentText?: string | null;
  emailConsent: boolean;
  smsConsent: boolean;
  onEmailChange: (checked: boolean) => void;
  onSmsChange: (checked: boolean) => void;
  /** True once the visitor has touched a box — gates the cleared-everything notice. */
  touched: boolean;
}

export function PublicFormConsent({
  showEmail,
  showSms,
  consentText,
  emailConsent,
  smsConsent,
  onEmailChange,
  onSmsChange,
  touched,
}: PublicFormConsentProps) {
  if (!showEmail && !showSms) return null;

  const noneSelected = !(showEmail && emailConsent) && !(showSms && smsConsent);

  return (
    <div className="mt-8 space-y-4 border-t border-slate-100 pt-6">
      {/* A real heading, not a styled <p>: this is a section of the form and has
          to sit in the outline beside the NDF section headers (issue #2197). */}
      <h2 className="text-sm font-medium text-slate-700">Communication Preferences</h2>

      {consentText && <p className="text-sm text-slate-500">{consentText}</p>}

      <p className="text-xs text-slate-600">
        Your selections here replace any preferences you&apos;ve given us before &mdash; anything
        you leave unchecked will be turned off.
      </p>

      <div className="space-y-3">
        {showEmail && (
          <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border border-slate-200 p-3 transition-colors hover:bg-slate-50">
            <Checkbox
              checked={emailConsent}
              onCheckedChange={(checked) => onEmailChange(!!checked)}
              aria-label="I agree to receive email communications"
            />
            <span className="text-sm text-slate-700">I agree to receive email communications</span>
          </label>
        )}

        {showSms && (
          <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border border-slate-200 p-3 transition-colors hover:bg-slate-50">
            <Checkbox
              checked={smsConsent}
              onCheckedChange={(checked) => onSmsChange(!!checked)}
              aria-label="I agree to receive SMS messages"
            />
            <span className="text-sm text-slate-700">I agree to receive SMS messages</span>
          </label>
        )}
      </div>

      {touched && noneSelected && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs text-amber-900">
            You haven&apos;t selected any communication preferences. If you previously opted in,
            submitting this form will opt you out of every option shown above.
          </p>
        </div>
      )}
    </div>
  );
}

export default PublicFormConsent;
